import { assertNever } from "../helper";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { AppLanguage, getLanguageStrings } from "internationalisation";
import { generateExternalSurveyTask, generateUploadsTask, generateWebPlatformTask, generateWebRequestTask } from "../tasks";
import { LangStrings } from "internationalisation/LangStrings";
import { RespondentExternalSurveyTask, RespondentWebPlatformTask, RespondentWebRequestTask } from "../types";

export const CART1025CintCollGroupIds = [
	"CART-460-1025-AU-WOOLWORTHS-DIGITAL"
] as const;

export const CART1025AmplifiedCollGroupIds = [
	"CART-461-1025-AU-WOOLWORTHS-ALL"
] as const;

export const CART1025CollGroupIds = [
	...CART1025CintCollGroupIds,
	...CART1025AmplifiedCollGroupIds
] as const;

export type CART1025CintCollGroupId = typeof CART1025CintCollGroupIds[number];
export type CART1025AmplifiedCollGroupId = typeof CART1025AmplifiedCollGroupIds[number];
export type CART1025CollGroupId = typeof CART1025CollGroupIds[number];

type CART1025CollConfig = {
	groupId: CART1025CollGroupId;
	metaFieldNames: ReadonlyArray<string>;
}

export const CART1025WebPlatformIds = [
	'CART-1025-AU-WOOLWORTHS-DIGITAL-HOME',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-YOGHURT',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-FROZENFISH',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-CHOCOLATE',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-SOFTDRINK',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-BATTERIES',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-NAPPIES',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-COFFEE',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-DETERGENT',
	'CART-1025-AU-WOOLWORTHS-DIGITAL-DEODORANT',

	'CART-1025-AU-WOOLWORTHS-ALL-HOME',
	'CART-1025-AU-WOOLWORTHS-ALL-YOGHURT',
	'CART-1025-AU-WOOLWORTHS-ALL-FROZENFISH',
	'CART-1025-AU-WOOLWORTHS-ALL-CHOCOLATE',
	'CART-1025-AU-WOOLWORTHS-ALL-SOFTDRINK',
	'CART-1025-AU-WOOLWORTHS-ALL-BATTERIES',
	'CART-1025-AU-WOOLWORTHS-ALL-NAPPIES',
	'CART-1025-AU-WOOLWORTHS-ALL-COFFEE',
	'CART-1025-AU-WOOLWORTHS-ALL-DETERGENT',
	'CART-1025-AU-WOOLWORTHS-ALL-DEODORANT',
];

export type CART1025WebPlatformId = typeof CART1025WebPlatformIds[number];

const getWebPlatformConfigUrl = (webPlatformId: CART1025WebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/CART-1025/${webPlatformId}.json`
}

const generateWebPlatformTasks = (
	groupId: CART1025CollGroupId
): RespondentWebPlatformTask[] => {

	switch (groupId) {
		case 'CART-460-1025-AU-WOOLWORTHS-DIGITAL':
			return CART1025WebPlatformIds.filter(id => id.includes('DIGITAL')).map(id => generateWebPlatformTask({
				groupId,
				platform: 'Woolworths',
				webPlatformConfigUrl: getWebPlatformConfigUrl(id),
				webPlatformID: id,
				taskLengthMinutes: "1", // This is for task description only. Actual task length is determined by the web platform config.
				useWebPlatformIDForTaskID: true,
				langStrings: getLanguageStrings(AppLanguage.en)
			}));
		case 'CART-461-1025-AU-WOOLWORTHS-ALL':
			return CART1025WebPlatformIds.filter(id => id.includes('ALL')).map(id => generateWebPlatformTask({
				groupId,
				platform: 'Woolworths',
				webPlatformConfigUrl: getWebPlatformConfigUrl(id),
				webPlatformID: id,
				taskLengthMinutes: "1", // This is for task description only. Actual task length is determined by the web platform config.
				useWebPlatformIDForTaskID: true,
				langStrings: getLanguageStrings(AppLanguage.en)
			}));
		default:
			assertNever(groupId);
		}
}

const generatePostViewTask = (
	config: CART1025CollConfig,
	langStrings: LangStrings
): RespondentExternalSurveyTask | RespondentWebRequestTask => {
	switch (config.groupId) {
		case 'CART-460-1025-AU-WOOLWORTHS-DIGITAL':
			return generateExternalSurveyTask({
				title: langStrings.Common.survey,
				description: langStrings.Survey.description,
				extraMetaFields: config.metaFieldNames,
				taskID: `${config.groupId}-SURVEY`,
				surveyUrl: 'https://survey.au1.qualtrics.com/jfe/form/SV_1Gs5prQimm98Dc2'
			});
		case 'CART-461-1025-AU-WOOLWORTHS-ALL':
    		return generateWebRequestTask({
				webRequestConfigUrl: `https://atrace-cdn.amplifiedintelligence.io/WebRequestConfigs/CART-1025/${config.groupId}_INCENTIVE.json`,
				params: {},
				taskID: config.groupId + '-WEBREQUEST',
			});
		default:
			assertNever(config.groupId);
	}
}

export const addCART1025CollConfig = (
	config: CART1025CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {
	const FINISH_MESSAGE = "🎉 Nice job, you’re all set! Your browsing session is complete and your data has been uploaded. Thanks for your time - your reward is on its way!"
	const langStrings = getLanguageStrings(AppLanguage.en);

	const postViewTask = generatePostViewTask(config, langStrings);

	const uploadTask = generateUploadsTask({
		config: {
			title: 'Uploads'
		},
		taskId: `${config.groupId}-UPLOADS`
	});

	builder.setFlag('deviceLock', 'mobile');
	builder.setFlag('requireMinimumBattery', 0.3);
	builder.setFlag('finishMessageOverride', FINISH_MESSAGE);
	builder.setFlag('preventTaskProgress', false);

	for (const webPlatformTask of generateWebPlatformTasks(config.groupId)) {
		builder.addTask(webPlatformTask);
	}

	builder.addTask(postViewTask);
	builder.addTask(uploadTask);

	return builder;
}